import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check, Ban, RotateCcw, Star } from "lucide-react";
import { adminApi, type VendorRow, type VendorStatus } from "@/lib/api";

const STATUS_LABEL: Record<VendorStatus, string> = {
  pending: "審査待ち",
  approved: "承認済み",
  suspended: "停止中",
  rejected: "却下",
};
const STATUS_STYLE: Record<VendorStatus, string> = {
  pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  suspended: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
  rejected: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
};

export function VendorsPage() {
  const qc = useQueryClient();
  const { data: vendors, isLoading, error } = useQuery({ queryKey: ["vendors"], queryFn: () => adminApi.listVendors() });
  const mut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: VendorStatus }) => adminApi.setVendorStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendors"] }),
  });

  return (
    <div className="px-4 py-4 space-y-3">
      <h2 className="text-lg font-semibold">業者審査</h2>
      <p className="text-xs text-ink-soft dark:text-white/60">承認済み業者だけが応札できます。審査待ちを承認してください。</p>
      {isLoading && <p className="text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> 読み込み中...</p>}
      {error && <p className="text-sm text-red-600">読み込みに失敗しました</p>}
      {vendors && vendors.length === 0 && <p className="text-sm text-ink-soft">登録業者がまだありません。</p>}
      <ul className="space-y-2">
        {vendors?.map((v) => (
          <VendorCard key={v.id} v={v} pending={mut.isPending} onSet={(status) => mut.mutate({ id: v.id, status })} />
        ))}
      </ul>
    </div>
  );
}

function VendorCard({ v, onSet, pending }: { v: VendorRow; onSet: (s: VendorStatus) => void; pending: boolean }) {
  return (
    <li className="rounded-xl border border-[var(--color-border)] p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{v.company || "（社名未設定）"}</p>
          <p className="text-xs text-ink-soft dark:text-white/60">
            {v.representative || "代表者未設定"} ／ {v.area || "エリア未設定"}
          </p>
          <p className="text-xs text-ink-soft dark:text-white/50">{v.email || ""}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLE[v.status]}`}>{STATUS_LABEL[v.status]}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-ink-soft dark:text-white/60">
        <Star size={12} className="text-yellow-500" />
        {(v.review_count ?? 0) > 0 ? `${(v.rating ?? 0).toFixed(1)}（${v.review_count}件）` : "評価なし"}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        {v.status !== "approved" && (
          <button disabled={pending} onClick={() => onSet("approved")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-500 text-white text-sm disabled:opacity-50">
            <Check size={14} /> 承認
          </button>
        )}
        {v.status === "approved" && (
          <button disabled={pending} onClick={() => onSet("suspended")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 dark:text-red-300 text-sm disabled:opacity-50">
            <Ban size={14} /> 停止
          </button>
        )}
        {(v.status === "suspended" || v.status === "rejected") && (
          <button disabled={pending} onClick={() => onSet("approved")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm disabled:opacity-50">
            <RotateCcw size={14} /> 復帰
          </button>
        )}
        {v.status === "pending" && (
          <button disabled={pending} onClick={() => onSet("rejected")} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm disabled:opacity-50">
            却下
          </button>
        )}
      </div>
    </li>
  );
}
